import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@kizen/kds/Typography';
import { Spacer } from '@kizen/kds/Spacer';
import Loader from '__components/Kizen/Loader';
import { toastVariant, useToast } from '__components/ToastProvider';
import useFormValidation from 'hooks/useFormValidation';
import { countriesQuery } from 'query/geography';
import { getChosenBusiness } from 'store/authentication/selectors';
import { serviceUpdateBusinessStart } from 'store/authentication/authenticationAction';
import { BusinessAddressFields } from 'pages/Settings/pages/Business/subpages/BusinessSettings/BusinessAddressFields';
import type { StepSubmitRef } from './AddCardModal';

export const BUSINESS_ADDRESS_FIELDS = [
  'street_address',
  'country',
  'state',
  'city',
  'postal_code',
] as const;

type BusinessAddressField = (typeof BUSINESS_ADDRESS_FIELDS)[number];

type BusinessAddress = Record<BusinessAddressField, string | null>;

const pickAddress = (business: Record<string, any>): BusinessAddress =>
  BUSINESS_ADDRESS_FIELDS.reduce(
    (acc, field) => ({ ...acc, [field]: business?.[field] ?? null }),
    {} as BusinessAddress
  );

// State is only required for countries with subdivisions, so it isn't part of the presence check
export const hasBusinessAddress = (business: Record<string, any>) =>
  Boolean(
    business?.street_address &&
      business?.country &&
      business?.city &&
      business?.postal_code
  );

type BusinessAddressStepProps = {
  submitRef: StepSubmitRef;
  onSaved: () => void;
};

export const BusinessAddressStep = (props: BusinessAddressStepProps) => {
  const { submitRef, onSaved } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [showToast] = useToast();
  const chosenBusiness = useSelector(getChosenBusiness);

  const [formData, setFormData] = useState(() => pickAddress(chosenBusiness));

  const {
    validateFormState,
    validationProps,
    handleInputChange,
    setValidationState,
  } = useFormValidation({
    setFormDirty: undefined,
    formState: formData,
    setFormState: setFormData,
  });

  const { data: countries, isLoading: loadingCountries } =
    useQuery(countriesQuery());

  const handleSave = useCallback(async () => {
    if (!validateFormState(t)) {
      return;
    }

    const [error] = await dispatch(
      serviceUpdateBusinessStart(pickAddress(formData))
    );

    if (!error) {
      onSaved();
      return;
    }

    const fieldErrors = error?.payload?.errors ?? {};
    const addressErrors = BUSINESS_ADDRESS_FIELDS.filter(
      (field) => fieldErrors[field]
    );

    if (addressErrors.length) {
      setValidationState(fieldErrors);
    }
    showToast({
      variant: toastVariant.FAILURE,
      message:
        Object.values(fieldErrors).join(', ') ||
        t('Failed to save business address'),
    });
  }, [
    validateFormState,
    t,
    dispatch,
    formData,
    onSaved,
    setValidationState,
    showToast,
  ]);

  useEffect(() => {
    submitRef.current = handleSave;
  }, [submitRef, handleSave]);

  if (loadingCountries) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader loading paddingTop="0" />
      </div>
    );
  }

  return (
    <div>
      <Typography weight="semibold">{t('Business Address')}</Typography>
      <Spacer size={15} />
      <BusinessAddressFields
        formData={formData}
        countries={countries}
        handleInputChange={handleInputChange}
        validationProps={validationProps}
        singleColumn
      />
    </div>
  );
};
